// Path: /home/jason/src/AgileView/autovision-ui/src/components/views/InferenceGalleryView.tsx

import React, { useEffect, useState } from 'react'
import { Col, Row, Spinner, Modal } from 'react-bootstrap'
import { RealTimeInferenceResult } from '../../types/prediction'
import RealTimeImage from '../RealTimeImage'

interface ImageData {
  url: string
  filename: string
  width: number
  height: number
  aspectRatio: number
}

interface InferenceGalleryViewProps {
  results: RealTimeInferenceResult[] | undefined
  dropFiles: File[] | null
  cardSize?: number
  isLoading?: boolean
}

const InferenceRealTimeGalleryView: React.FC<InferenceGalleryViewProps> = ({
  results,
  dropFiles,
  cardSize = 512,
  isLoading = false,
}) => {
  const [images, setImages] = useState<ImageData[]>([])
  const [selectedImage, setSelectedImage] = useState<{
    result: RealTimeInferenceResult
    imageData: ImageData
  } | null>(null)

  useEffect(() => {
    const loadImage = (file: File): Promise<ImageData> => {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file)
        const img = new Image()

        img.onload = () => {
          resolve({
            url,
            filename: file.name,
            width: img.naturalWidth,
            height: img.naturalHeight,
            aspectRatio: img.naturalWidth / img.naturalHeight,
          })
        }

        img.src = url
      })
    }

    const urls: string[] = []
    const loadImages = async () => {
      if (!dropFiles) return
      const loadedImages = await Promise.all(dropFiles.map(loadImage))
      loadedImages.forEach((img) => urls.push(img.url))
      setImages(loadedImages)
    }

    loadImages()

    return () => {
      // Cleanup: revoke all URLs created in this effect
      urls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [dropFiles]) // Remove images from dependency array

  const handleImageClick = (result: RealTimeInferenceResult, imageData: ImageData) => {
    setSelectedImage({ result, imageData })
  }

  const handleCloseModal = () => {
    setSelectedImage(null)
  }

  return (
    <>
      <Row style={{ height: '100%' }}>
        <Col style={{ height: '100%' }}>
          {isLoading ? (
            <Spinner animation="border" variant="primary" />
          ) : results?.length ? (
            <div className="grid-view" style={{ height: '100%', overflow: 'auto' }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  padding: '16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {results
                  .filter((result) => result.predictions.length > 0)
                  .map((result: RealTimeInferenceResult, index) => {
                    const image = images.find((img) => img.filename === result.filename)
                    if (!image) return null

                    return (
                      <div
                        key={index}
                        onClick={() => handleImageClick(result, image)}
                        style={{ cursor: 'pointer' }}
                      >
                        <RealTimeImage
                          result={result}
                          imageData={{
                            url: image.url,
                            width: image.width,
                            height: image.height,
                          }}
                          cardSize={cardSize}
                        />
                      </div>
                    )
                  })}
              </div>
            </div>
          ) : (
            <div className="no-results-message">
              <i className="fas fa-search me-2"></i>
              <p>No objects were detected in the uploaded images.</p>
              <p className="text-muted">
                Try uploading different images or adjusting the model&apos;s confidence threshold.
              </p>
            </div>
          )}
        </Col>
      </Row>

      {/* Image Inspection Modal */}
      <Modal
        show={!!selectedImage}
        onHide={handleCloseModal}
        size="xl"
        centered
        className="image-inspection-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Inspection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImage && (
            <RealTimeImage
              result={selectedImage.result}
              imageData={{
                url: selectedImage.imageData.url,
                width: selectedImage.imageData.width,
                height: selectedImage.imageData.height,
              }}
              cardSize={selectedImage.imageData.width}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default InferenceRealTimeGalleryView
